import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, BookOpen, Clock, ShieldCheck } from 'lucide-react';
const COVER_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/9UeoFfosAJa86qsEFsQomN/heylloyd-image-1778283515751.png';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.8,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  return <div className="min-h-screen bg-cream text-charcoal selection:bg-sage selection:text-white overflow-x-hidden" data-id="element-1">
      {/* 1. NAVBAR */}
      <nav className="fixed top-0 w-full z-50 bg-cream/90 backdrop-blur-md border-b border-charcoal/5" data-id="element-2">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-3">
          <span className="font-serif text-xl font-medium tracking-tight text-charcoal" data-id="element-4">
            Plan Adiós Inflamación
          </span>
          <a href="#oferta" className="bg-terracotta hover:bg-terracotta-hover text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-300" data-id="element-5">
            Obtener el libro
          </a>
        </div>
      </nav>

      {/* 2. HERO */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6" data-id="element-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-8 items-center" data-id="element-7">
          <div className="order-2 md:order-1" data-id="element-8">
            <FadeIn data-id="element-9">
              <span className="text-sage font-medium tracking-wider text-sm uppercase mb-4 block" data-id="element-10">
                Para la mujer chilena contemporánea
              </span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-charcoal mb-6" data-id="element-11">
                Apaga el interruptor del cortisol y recupera tu vientre plano
              </h1>
              <p className="text-lg md:text-xl text-charcoal-light mb-8 max-w-lg leading-relaxed" data-id="element-12">
                El protocolo de 14 días diseñado para sanar tu eje
                intestino-cerebro, detener la inflamación por estrés y
                devolverte la energía.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center" data-id="element-13">
                <a href="#oferta" className="bg-terracotta hover:bg-terracotta-hover text-white px-8 py-4 rounded-full text-base font-medium transition-all duration-300 flex items-center gap-2 group w-full sm:w-auto justify-center shadow-lg shadow-terracotta/20" data-id="element-14">
                  Quiero el protocolo
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-15" />
                </a>
                <a href="#carta" className="text-charcoal hover:text-terracotta font-medium underline underline-offset-4 decoration-charcoal/30 hover:decoration-terracotta transition-colors px-4 py-2" data-id="element-16">
                  Leer la carta de la autora
                </a>
              </div>
              <div className="mt-8 flex items-center gap-2 text-sm text-charcoal-light" data-id="element-17">
                <ShieldCheck className="w-4 h-4 text-sage" data-id="element-18" />
                <span data-id="element-19">Basado en el eje intestino-cerebro</span>
              </div>
            </FadeIn>
          </div>

          <div className="order-1 md:order-2 flex justify-center md:justify-end relative" data-id="element-20">
            <FadeIn delay={0.2} data-id="element-21">
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative z-10" data-id="element-22">
                <img src={COVER_IMAGE_URL} alt="Portada del libro Plan Adiós Inflamación" className="w-[280px] md:w-[380px] rounded-r-xl shadow-book rotate-2" data-id="element-23" />
              </motion.div>
              {/* Decorative background shape */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[400px] md:h-[400px] bg-cream-dark rounded-full blur-3xl -z-10 opacity-60" data-id="element-24"></div>
            </FadeIn>
          </div>
        </div>

        <div className="hidden md:flex justify-center mt-24" data-id="element-25">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-26">
            <ChevronDown className="w-6 h-6 text-charcoal/30" data-id="element-27" />
          </motion.div>
        </div>
      </section>

      {/* 3. CARTA DE LA AUTORA */}
      <section id="carta" className="py-24 bg-white px-6 relative" data-id="element-28">
        <div className="max-w-2xl mx-auto" data-id="element-29">
          <FadeIn data-id="element-30">
            <div className="mb-12 flex justify-center" data-id="element-31">
              <span className="text-terracotta text-4xl font-serif" data-id="element-32">✽</span>
            </div>
            <div className="prose prose-lg md:prose-xl prose-p:font-serif prose-p:text-charcoal-light prose-p:leading-relaxed prose-p:mb-6" data-id="element-33">
              <p className="first-letter:text-5xl first-letter:font-serif first-letter:text-terracotta first-letter:mr-2 first-letter:float-left" data-id="element-34">
                Sé exactamente cómo te sientes ahora mismo. Sé lo que es
                despertar con el vientre plano y, apenas cuatro horas después —o
                tras un café rápido y una mañana estresante en el trabajo—
                sentir que tu pantalón te aprieta tanto que te falta el aire.
              </p>
              <p data-id="element-35">
                Sé lo que es elegir la ropa no por cómo te queda de linda, sino
                por qué tan bien esconde esa hinchazón que parece aparecer de la
                nada. Pasé años pensando que "comía sano". Comía ensaladas,
                evitaba el azúcar y me mataba en el gimnasio, pero mi abdomen
                seguía pareciendo un globo a punto de reventar.
              </p>
              <p data-id="element-36">
                La frustración no era solo estética; era una pesadez física que
                me quitaba la chispa y una neblina mental que no me dejaba
                rendir. Este protocolo nació de la necesidad de dejar de pelear
                contra los síntomas y empezar a entender la causa: el cortisol.
              </p>
              <p data-id="element-37">
                En Chile vivimos a mil por hora, y nuestro sistema digestivo es
                el primero en pagar la cuenta. Este libro no es una dieta; es la
                herramienta que yo necesité para recuperar el control de mi
                cuerpo. Me alegra que hoy esté en tus manos. Estás a punto de
                descubrir que no estás "hinchada por naturaleza", solo necesitas
                apagar el incendio interno.
              </p>
            </div>
            <div className="mt-12 text-right" data-id="element-38">
              <p className="font-serif text-2xl text-charcoal italic" data-id="element-39">
                Con cariño,
              </p>
              <p className="font-sans font-medium text-sage mt-2" data-id="element-40">La Autora</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. LA ESCENA QUE CONOCES BIEN */}
      <section className="py-24 px-6 bg-cream-dark" data-id="element-41">
        <div className="max-w-5xl mx-auto" data-id="element-42">
          <FadeIn data-id="element-43">
            <div className="grid md:grid-cols-12 gap-12 items-center" data-id="element-44">
              <div className="md:col-span-5" data-id="element-45">
                <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-6 leading-tight" data-id="element-46">
                  La Escena que Conoces Bien
                </h2>
                <div className="w-12 h-1 bg-terracotta mb-8" data-id="element-47"></div>
                <p className="text-lg text-charcoal-light leading-relaxed" data-id="element-48">
                  Estás aquí porque estás cansada. No solo cansada de la
                  pesadez, sino cansada de intentar soluciones que no funcionan.
                  Probaste el "jugo verde" de moda, probaste dejar los
                  carbohidratos, probaste el ayuno intermitente extremo y, sin
                  embargo, el espejo te sigue devolviendo la misma imagen de
                  incomodidad al final del día.
                </p>
              </div>
              <div className="md:col-span-7 bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-charcoal/5 relative" data-id="element-49">
                <div className="absolute -top-6 -left-6 text-sage/20 text-8xl font-serif leading-none" data-id="element-50">
                  "
                </div>
                <p className="font-serif text-xl md:text-2xl text-charcoal leading-relaxed relative z-10 italic" data-id="element-51">
                  Imagina esto: son las 7 de la tarde. Has tenido un día intenso
                  entre reuniones, correos, tacos y responsabilidades
                  familiares. Llegas a casa y lo primero que haces no es
                  saludar, sino desabotonarte el pantalón.
                </p>
                <p className="mt-6 text-charcoal-light font-medium" data-id="element-52">
                  Sientes un alivio inmediato, pero luego viene la decepción.
                  Miras hacia abajo y ves esa inflamación persistente. Te
                  sientes pesada, lenta y, sinceramente, un poco traicionada por
                  tu propio cuerpo. ¿Cómo es posible si "te cuidaste" todo el
                  día?
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5. LA PROMESA ROTA */}
      <section className="py-24 px-6 bg-cream" data-id="element-53">
        <div className="max-w-4xl mx-auto text-center" data-id="element-54">
          <FadeIn data-id="element-55">
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-8" data-id="element-56">
              La Promesa Rota
            </h2>
            <p className="text-xl text-charcoal-light leading-relaxed mb-8" data-id="element-57">
              La industria del bienestar nos ha vendido que la inflamación se
              soluciona solo con comida. "Come esto, no comas aquello". Pero
              para la mujer moderna, esa es solo la mitad de la ecuación.
            </p>
            <div className="bg-sage/5 border border-sage/20 rounded-2xl p-8 md:p-10 text-left" data-id="element-58">
              <p className="text-lg text-charcoal leading-relaxed mb-6" data-id="element-59">
                El problema de la mayoría de los métodos es que ignoran el
                factor estresor. Si sigues una dieta estricta que te genera
                ansiedad, tu cuerpo produce más cortisol.
              </p>
              <p className="text-lg text-charcoal leading-relaxed mb-6 font-medium" data-id="element-60">
                El cortisol alto detiene la digestión, debilita la pared
                intestinal y activa una respuesta inflamatoria sistémica.
              </p>
              <div className="bg-white p-6 rounded-xl border-l-4 border-terracotta shadow-sm" data-id="element-61">
                <p className="font-serif text-xl text-terracotta" data-id="element-62">
                  En resumen: tu dieta te está inflamando más porque tu estilo
                  de vida está en modo supervivencia.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. EL MECANISMO ÚNICO */}
      <section className="py-24 px-6 bg-charcoal text-cream" data-id="element-63">
        <div className="max-w-5xl mx-auto" data-id="element-64">
          <FadeIn data-id="element-65">
            <div className="text-center mb-16" data-id="element-66">
              <span className="text-terracotta font-medium tracking-wider text-sm uppercase mb-4 block" data-id="element-67">
                El Mecanismo Único
              </span>
              <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-68">
                El Interruptor del Cortisol
              </h2>
              <p className="text-xl text-cream/80 max-w-2xl mx-auto leading-relaxed" data-id="element-69">
                Utilizaremos un método que llamo el "Protocolo de Reseteo
                Circadiano". No se trata solo de qué comes, sino de cuándo y
                cómo lo haces para que tu sistema nervioso le dé permiso a tu
                sistema digestivo para trabajar.
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16" data-id="element-70">
              <div className="bg-cream/10 backdrop-blur-sm p-8 rounded-2xl w-full md:w-72 text-center border border-cream/10" data-id="element-71">
                <div className="w-16 h-16 bg-terracotta/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-72">
                  <span className="text-2xl" data-id="element-73">🚨</span>
                </div>
                <h3 className="font-serif text-xl mb-2" data-id="element-74">Alerta Permanente</h3>
                <p className="text-sm text-cream/60" data-id="element-75">
                  Modo supervivencia, digestión bloqueada, inflamación activa.
                </p>
              </div>

              <div className="hidden md:block text-terracotta" data-id="element-76">
                <ArrowRight className="w-8 h-8" data-id="element-77" />
              </div>
              <div className="md:hidden text-terracotta rotate-90" data-id="element-78">
                <ArrowRight className="w-8 h-8" data-id="element-79" />
              </div>

              <div className="bg-sage p-8 rounded-2xl w-full md:w-72 text-center shadow-xl shadow-sage/20" data-id="element-80">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-81">
                  <span className="text-2xl" data-id="element-82">🌱</span>
                </div>
                <h3 className="font-serif text-xl mb-2 text-white" data-id="element-83">
                  Calma Metabólica
                </h3>
                <p className="text-sm text-white/80" data-id="element-84">
                  Digestión fluida, desinflamación natural, energía constante.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. ¿POR QUÉ ESTAMOS INFLAMADAS? */}
      <section className="py-24 px-6 bg-cream" data-id="element-85">
        <div className="max-w-6xl mx-auto" data-id="element-86">
          <FadeIn data-id="element-87">
            <div className="text-center mb-16" data-id="element-88">
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-6" data-id="element-89">
                ¿Por qué estamos inflamadas?
              </h2>
              <p className="text-lg text-charcoal-light max-w-2xl mx-auto" data-id="element-90">
                La inflamación es, en realidad, una respuesta de defensa de tu
                cuerpo. El problema es que en la vida moderna, el cuerpo
                interpreta el estrés de un correo electrónico urgente de la
                misma manera que interpretaría el ataque de un depredador.
                Cuando estás bajo estrés crónico, ocurren tres cosas fatales
                para tu abdomen:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-91">
              {[{
              num: '01',
              title: 'Hipo-motilidad',
              desc: 'Tu estómago deja de procesar la comida con eficiencia porque el cuerpo prioriza enviar sangre a tus músculos para "huir". La comida se estanca y fermenta. Hola, gases.'
            }, {
              num: '02',
              title: 'Permeabilidad Intestinal',
              desc: 'El cortisol alto "abre" las uniones de tu pared intestinal, permitiendo que toxinas pasen al torrente sanguíneo, activando a tu sistema inmune.'
            }, {
              num: '03',
              title: 'Retención de Sodio',
              desc: 'El estrés altera la aldosterona, haciendo que te sientas como una esponja de agua.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.2,
              duration: 0.6
            }} className="bg-white p-8 rounded-2xl border border-charcoal/5 shadow-sm hover:shadow-md transition-shadow" data-id="element-92">
                  <span className="font-serif text-4xl text-sage/30 block mb-4" data-id="element-93">
                    {item.num}
                  </span>
                  <h3 className="font-serif text-xl text-charcoal mb-4" data-id="element-94">
                    {item.title}
                  </h3>
                  <p className="text-charcoal-light leading-relaxed" data-id="element-95">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. TUS 3 ERRORES MÁS COMUNES */}
      <section className="py-24 px-6 bg-white" data-id="element-96">
        <div className="max-w-4xl mx-auto" data-id="element-97">
          <FadeIn data-id="element-98">
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-16 text-center" data-id="element-99">
              Tus 3 Errores Más Comunes
            </h2>

            <div className="space-y-12" data-id="element-100">
              {[{
              num: '1',
              title: 'Exceso de Fibra Cruda',
              desc: 'Intentar desinflamarte comiendo baldes de ensalada de lechuga y brócoli crudo a la cena. Tu intestino inflamado no puede procesar esa celulosa, generando más distensión.'
            }, {
              num: '2',
              title: 'Café en Ayunas',
              desc: 'El café con el estómago vacío dispara un pico de cortisol que le dice a tu cuerpo que comience el día en modo pánico.'
            }, {
              num: '3',
              title: 'Cardio de Alta Intensidad',
              desc: 'Correr una hora cuando ya estás agotada solo le añade más leña al fuego de la inflamación.'
            }].map((error, i) => <div key={i} className={`flex flex-col md:flex-row gap-6 md:gap-12 items-start ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-101">
                  <div className="flex-shrink-0 w-16 h-16 bg-cream-dark rounded-full flex items-center justify-center font-serif text-2xl text-terracotta" data-id="element-102">
                    {error.num}
                  </div>
                  <div data-id="element-103">
                    <h3 className="font-serif text-2xl text-charcoal mb-3" data-id="element-104">
                      {error.title}
                    </h3>
                    <p className="text-lg text-charcoal-light leading-relaxed" data-id="element-105">
                      {error.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 10. LA ILUSIÓN DEL PESO (Hook Section) */}
      <section className="py-32 px-6 bg-terracotta text-white text-center relative overflow-hidden" data-id="element-106">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-107"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-108">
          <FadeIn data-id="element-109">
            <span className="text-white/70 font-medium tracking-wider text-sm uppercase mb-8 block" data-id="element-110">
              La Ilusión del Peso
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-10" data-id="element-111">
              "La grasa no aparece y desaparece en 8 horas; la inflamación sí."
            </h2>
            <p className="text-xl md:text-2xl text-white/90 font-serif italic max-w-3xl mx-auto" data-id="element-112">
              Estamos ante un enemigo que no se quema en el gimnasio, se apaga
              con inteligencia biológica.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 9. LO QUE VAS A APRENDER */}
      <section className="py-24 px-6 bg-cream-dark" data-id="element-113">
        <div className="max-w-4xl mx-auto" data-id="element-114">
          <FadeIn data-id="element-115">
            <div className="bg-white rounded-3xl p-8 md:p-16 shadow-sm border border-charcoal/5" data-id="element-116">
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-10 text-center" data-id="element-117">
                Lo que vas a lograr con este plan
              </h2>

              <ul className="space-y-6" data-id="element-118">
                {['Identificar la diferencia entre grasa abdominal e inflamación por estrés.', 'Entender por qué los métodos tradicionales fallan en mujeres estresadas.', 'Reconocer las señales de alerta de una inflamación crónica.', 'Aplicar el Protocolo de Reseteo Circadiano paso a paso durante 14 días.', 'Sustituir los 3 errores comunes por hábitos que promueven la calma metabólica.', 'Recuperar tu energía, claridad mental y, por supuesto, tu vientre plano.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-119">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-sage/10 flex items-center justify-center" data-id="element-120">
                      <Check className="w-4 h-4 text-sage" data-id="element-121" />
                    </div>
                    <span className="text-lg text-charcoal-light" data-id="element-122">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 11. CTA / OFFER CARD */}
      <section id="oferta" className="py-24 px-6 bg-cream scroll-mt-20" data-id="element-123">
        <div className="max-w-3xl mx-auto" data-id="element-124">
          <FadeIn data-id="element-125">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-charcoal/5 border border-charcoal/5 text-center relative overflow-hidden" data-id="element-126">
              <div className="absolute top-0 left-0 w-full h-2 bg-sage" data-id="element-127"></div>

              <img src={COVER_IMAGE_URL} alt="Plan Adiós Inflamación" className="w-48 mx-auto rounded-lg shadow-book mb-8" data-id="element-128" />

              <h2 className="font-serif text-3xl text-charcoal mb-2" data-id="element-129">
                Plan Adiós Inflamación
              </h2>
              <p className="text-charcoal-light mb-8" data-id="element-130">
                El Protocolo de 14 Días para Recuperar tu Vientre Plano y tu
                Energía
              </p>

              <div className="bg-cream-dark rounded-xl p-6 mb-8 text-left inline-block w-full max-w-md" data-id="element-131">
                <h4 className="font-medium text-charcoal mb-4 flex items-center gap-2" data-id="element-132">
                  <BookOpen className="w-5 h-5 text-terracotta" data-id="element-133" />
                  ¿Qué incluye?
                </h4>
                <ul className="space-y-3 text-sm text-charcoal-light" data-id="element-134">
                  <li className="flex items-center gap-2" data-id="element-135">
                    <Check className="w-4 h-4 text-sage" data-id="element-136" /> Libro digital
                    completo (PDF)
                  </li>
                  <li className="flex items-center gap-2" data-id="element-137">
                    <Check className="w-4 h-4 text-sage" data-id="element-138" /> Protocolo de Reseteo
                    Circadiano de 14 días
                  </li>
                  <li className="flex items-center gap-2" data-id="element-139">
                    <Check className="w-4 h-4 text-sage" data-id="element-140" /> Guía de alimentación
                    anti-estrés
                  </li>
                  <li className="flex items-center gap-2" data-id="element-141">
                    <Check className="w-4 h-4 text-sage" data-id="element-142" /> Acceso inmediato
                    desde cualquier dispositivo
                  </li>
                </ul>
              </div>

              <div className="mb-8 flex flex-col items-center gap-2" data-id="element-143">
                <div className="flex items-baseline gap-3" data-id="element-144">
                  <span className="text-xl font-sans text-charcoal-light line-through decoration-terracotta/60 decoration-2" data-id="element-145">
                    $14.990 CLP
                  </span>
                  <span className="bg-sage/10 text-sage text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider" data-id="element-146">
                    -60%
                  </span>
                </div>
                <span className="text-5xl font-serif text-charcoal" data-id="element-147">
                  $5.990{' '}
                  <span className="text-2xl text-charcoal-light" data-id="element-148">CLP</span>
                </span>
                <span className="text-xs text-charcoal-light" data-id="element-149">
                  Pago único · Sin suscripciones
                </span>
              </div>

              <a href="https://empresagama.com/cart/47340660097173:1" target="_blank" rel="noopener noreferrer" className="inline-block w-full md:w-auto bg-terracotta hover:bg-terracotta-hover text-white px-10 py-4 rounded-full text-lg font-medium transition-all duration-300 shadow-lg shadow-terracotta/20 mb-6 text-center" data-id="element-150">
                Obtener mi protocolo ahora
              </a>

              <div className="flex items-center justify-center gap-6 text-xs text-charcoal-light font-medium" data-id="element-151">
                <span className="flex items-center gap-1" data-id="element-152">
                  <Clock className="w-3 h-3" data-id="element-153" /> Acceso inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-154">
                  <BookOpen className="w-3 h-3" data-id="element-155" /> PDF descargable
                </span>
                <span className="flex items-center gap-1" data-id="element-156">
                  <ShieldCheck className="w-3 h-3" data-id="element-157" /> Garantía de 7 días
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 12. FOOTER */}
      <footer className="bg-charcoal text-white/60 py-12 px-6 text-xs text-center" data-id="element-158">
        <div className="max-w-4xl mx-auto" data-id="element-159">
          <p className="mb-6 leading-relaxed" data-id="element-160">
            [DESCARGO DE RESPONSABILIDAD LEGAL] Este libro digital, "Plan Adiós
            Inflamación", tiene un propósito puramente informativo y educativo.
            El contenido aquí expuesto no pretende diagnosticar, tratar, curar
            ni prevenir ninguna enfermedad, ni sustituir el consejo, diagnóstico
            o tratamiento de un profesional médico calificado. Los resultados
            individuales pueden variar y dependen del punto de partida de cada
            persona, su compromiso y su metabolismo único. Se recomienda
            encarecidamente consultar con su médico antes de realizar cambios
            significativos en su dieta o estilo de vida, especialmente si está
            embarazada, amamantando, toma medicamentos o tiene condiciones de
            salud preexistentes. El autor y los editores no se hacen
            responsables de cualquier uso inapropiado de la información
            contenida en esta guía.
          </p>
          <p data-id="element-161">
            © {new Date().getFullYear()} Plan Adiós Inflamación. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}